import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import useLanguage from "../../hooks/useLanguage";
import useRecordings from "../../hooks/useRecordings";
import useToaster from "../../hooks/useToaster";

import CommonPage from "../../components/common-page";
import { describe, fmtDateIL, fmtTimeIL, fmtSize } from "./utils";

const MAX_RERUN = 5;

// Same per-status palette as the highlights runs drawer, so a run reads the
// same on both pages.
const RUN_COLORS = {
  running: { bg: "#DBE7FF", fg: "#1A3B7F" },
  completed: { bg: "#D6F5E2", fg: "#0F5E2E" },
  empty: { bg: "#EBEBEB", fg: "#5A5A5A" },
  failed: { bg: "#FFD6D6", fg: "#7A1A1A" },
};

function RunLine({ run, text }) {
  const status = run.status || "running";
  const color = RUN_COLORS[status] || RUN_COLORS.running;
  const counts =
    run.clips_uploaded != null || run.clips_found != null
      ? `${run.clips_uploaded ?? 0}/${run.clips_found ?? 0} ${text.runs.clips}`
      : "";

  return (
    <Box sx={{ py: 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Chip
          dir="ltr"
          size="small"
          label={text.runs.status[status] || status}
          sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600 }}
        />
        {counts ? (
          <Typography variant="caption" dir="ltr" sx={{ unicodeBidi: "isolate" }}>
            {counts}
          </Typography>
        ) : null}
        {run.rerun_of ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<ReplayRoundedIcon fontSize="small" />}
            label={text.runs.rerun}
          />
        ) : null}
        <Typography
          variant="caption"
          dir="ltr"
          sx={{
            marginInlineStart: "auto",
            color: "text.secondary",
            unicodeBidi: "isolate",
          }}
        >
          {fmtDateIL(run.started_at)} · {fmtTimeIL(run.started_at)}
          {run.finished_at ? ` → ${fmtTimeIL(run.finished_at)}` : ""}
        </Typography>
      </Box>
      {run.error ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {run.error}
        </Typography>
      ) : null}
    </Box>
  );
}

function RecordingRow({ item, text, checked, disabled, onToggle, expanded, onExpand }) {
  const { date, window: recWindow } = describe(item);
  const runs = item.runs || [];

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onChange={() => onToggle(item.key)}
          />
        </TableCell>
        <TableCell dir="ltr" sx={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
          {date}
        </TableCell>
        <TableCell dir="ltr" sx={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
          {recWindow}
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          {item.meta?.show_title || (
            <Typography
              variant="caption"
              dir="ltr"
              sx={{ color: "text.secondary", unicodeBidi: "isolate" }}
            >
              {item.key}
            </Typography>
          )}
        </TableCell>
        <TableCell dir="ltr" sx={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
          {fmtSize(item.size)}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
            {runs.length ? `${runs.length} ${text.runs.label}` : ""}
          </Typography>
          <IconButton size="small" onClick={() => onExpand(item.key)}>
            {expanded ? (
              <ExpandMoreRoundedIcon
                fontSize="small"
                sx={{ transform: "rotate(180deg)" }}
              />
            ) : (
              <PlayCircleOutlineRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "action.hover" }}>
              <video
                controls
                preload="none"
                src={item.url}
                style={{ width: "100%", maxWidth: 640, borderRadius: 6 }}
              />
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                {text.runs.title}
              </Typography>
              <Divider />
              {runs.length ? (
                runs.map((run, i) => (
                  <Box key={run.run_id}>
                    {i > 0 ? <Divider /> : null}
                    <RunLine run={run} text={text} />
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ py: 1.5, color: "text.secondary" }}
                >
                  {text.runs.empty}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Recordings() {
  const [
    {
      pages: { recordings: text },
    },
  ] = useLanguage();
  const { listRecordings, rerunRecordings } = useRecordings();
  const { addToaster } = useToaster();

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [launching, setLaunching] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await listRecordings();
      setItems(response?.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) =>
    setSelected((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );

  const handleRerun = async () => {
    setLaunching(true);
    try {
      const response = await rerunRecordings(selected);
      const launched = response?.launched?.length || 0;
      const errors = response?.errors || [];
      if (launched)
        addToaster({
          message: text.launched.replace("{n}", launched),
          severity: "success",
        });
      if (errors.length)
        addToaster({
          message: `${text.launchFailed.replace("{n}", errors.length)}: ${errors
            .map((e) => e.error)
            .join(", ")}`,
          severity: "error",
        });
      setSelected([]);
      await fetchItems();
    } catch (_) {
      /* toaster raised by hook */
    } finally {
      setLaunching(false);
    }
  };

  if (initialLoad && loading)
    return (
      <CommonPage title={text.title} subtitle={text.subtitle}>
        <Skeleton variant="rounded" height={36} sx={{ mb: 2, maxWidth: 260 }} />
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />
        ))}
      </CommonPage>
    );

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      action={{
        label: text.refresh,
        icon: <RefreshRoundedIcon fontSize="small" />,
        onClick: fetchItems,
        disabled: loading,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          disabled={!selected.length || launching}
          startIcon={
            launching ? (
              <CircularProgress size={14} />
            ) : (
              <ReplayRoundedIcon fontSize="small" />
            )
          }
          onClick={handleRerun}
        >
          {text.rerun}
        </Button>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {text.selected
            .replace("{n}", selected.length)
            .replace("{max}", MAX_RERUN)}
        </Typography>
      </Box>

      {!items.length ? (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="h6">{text.empty.title}</Typography>
          <Typography variant="body2">{text.empty.subtitle}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{text.columns.date}</TableCell>
                <TableCell>{text.columns.window}</TableCell>
                <TableCell>{text.columns.show}</TableCell>
                <TableCell>{text.columns.size}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <RecordingRow
                  key={item.key}
                  item={item}
                  text={text}
                  checked={selected.includes(item.key)}
                  disabled={
                    selected.length >= MAX_RERUN && !selected.includes(item.key)
                  }
                  onToggle={toggle}
                  expanded={expanded === item.key}
                  onExpand={(key) =>
                    setExpanded((cur) => (cur === key ? null : key))
                  }
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CommonPage>
  );
}
